import { Box, Chip, CircularProgress, Grid, Stack } from "@mui/material";
import {
  MediaFile,
  PopulatedProjectFile,
  ProjectFile,
  SpecialRequirements,
} from "../../../../__generated__/types";
import { useFormState } from "../../../../hooks/use-form-state";
import { AddProjectForm } from "../add-project-form";
import { UploadProjectImage } from "../upload-project-image";
import { Body2Typography } from "../../../../components/typography";
import { useState, useEffect, useCallback, useRef } from "react";
import { useCreateProjectFileMutation } from "../../../../hooks/use-create-project-file-mutation";
import { useUserContext } from "../../../../hooks/userContext";
import { useFirebaseImageUpload } from "../../../../components/firebase-image-upload/use-firebase-image-upload";
import { FirebasePaths } from "../../../../utils/firebase";
import { useUpdateProjectFileMutation } from "../../../../hooks/use-update-project-file-mutation";
import { ProjectImageList } from "../project-image-list";
import { useDeleteFirebaseImage } from "../../../../components/firebase-image-upload/use-delete-firebase-image";
import { useNavigate } from "react-router";
import { Pages } from "../../../../utils";
import { useNotification } from "../../../../contexts/NotificationContext";
import { useGetDesignerProjectFiles } from "../../../../hooks/use-get-designer-project-files";
import { ImageDetailsDialog } from "../../../../components/image-details-dialog";
import { useUpdateMediaFileMutation } from "../../../../hooks/use-update-media-file-mutation";

export type LocalProjectFileImage = {
  _id?: string;
  file?: File;
  local_url: string;
  title?: string;
  description?: string;
  credit?: string;
};
const MAX_PROJECT_IMAGES = 15;

const initialState: ProjectFile = {
  name: "",
  style: "",
  projectCategory: "",
  specialRequirements: [],
  budgetRange: "",
  squareFootage: "",
  projectDuration: "",
  images: [],
} as unknown as ProjectFile;

export const AddProjectContainer = ({
  projectFile,
  handleSubmitRef,
  handlePublishRef,
  onPublishStateChange,
  onSetFormValid,
}: {
  projectFile?: PopulatedProjectFile | null;
  handleSubmitRef?: React.MutableRefObject<(() => void) | null>;
  handlePublishRef?: React.MutableRefObject<((nextPublished: boolean) => void) | null>;
  onPublishStateChange?: (published: boolean) => void;
  onSetFormValid?: (isValid: boolean) => void;
}) => {
  const { loading, onUpload } = useFirebaseImageUpload(
    FirebasePaths.PROJECT_FILES
  );
  const { createProjectFile, loading: projectFileSaveLoading } =
    useCreateProjectFileMutation();
  const { user } = useUserContext();
  const { updateProjectFile, loading: projectFileUpdateLoading } =
    useUpdateProjectFileMutation();
  const { deleteFirebaseImage, loading: deleteImageLoading } =
    useDeleteFirebaseImage();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { getDesignerProjectFiles: refreshDesignerProjectFiles } =
    useGetDesignerProjectFiles();
  const isEditMode = !!projectFile;
  const publishOverrideRef = useRef<boolean | null>(null);
  const [isPublished, setIsPublished] = useState<boolean>(() => {
    if (!projectFile) return false;
    return projectFile.published ?? true;
  });

  const initialLocalImages = (projectFile?.images as MediaFile[])?.slice(0, MAX_PROJECT_IMAGES).map(
    (image) => ({
      _id: image._id as string,
      local_url: image.url as string,
      title: image.title || undefined,
      description: image.description || undefined,
      credit: (image as any)?.credit || undefined,
    })
  );
  const [localImages, setLocalImages] = useState<LocalProjectFileImage[]>(
    initialLocalImages || []
  );

  // Image details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);
  const { updateMediaFile } = useUpdateMediaFileMutation();

  const handleAddImages = useCallback(
    (incoming: { file: File; local_url: string }[]) => {
      if (!incoming || incoming.length === 0) {
        return;
      }
      setLocalImages((current) => {
        if (current.length >= MAX_PROJECT_IMAGES) {
          showError(
            `You can upload up to ${MAX_PROJECT_IMAGES} images for this project.`
          );
          return current;
        }

        const remaining = MAX_PROJECT_IMAGES - current.length;
        const accepted = incoming.slice(0, remaining).map((image) => ({
          ...image,
        }));

        if (accepted.length < incoming.length) {
          showError(
            `Only ${remaining} more image${remaining === 1 ? "" : "s"} can be added (maximum ${MAX_PROJECT_IMAGES}).`
          );
        }

        return [...current, ...accepted];
      });
    },
    [showError]
  );

  const handleDeleteImage = useCallback((index: number) => {
    setLocalImages((current) => {
      const target = current[index];
      if (target?.file) {
        URL.revokeObjectURL(target.local_url);
      }
      return current.filter((_, i) => i !== index);
    });
  }, []);

  const handleSetCoverImage = useCallback((index: number) => {
    setLocalImages((current) => {
      if (index <= 0 || index >= current.length) {
        return current;
      }
      const updated = [...current];
      const [selected] = updated.splice(index, 1);
      updated.unshift(selected);
      return updated;
    });
  }, []);

  const handleEditImageDetails = useCallback((index: number) => {
    setEditingImageIndex(index);
    setDetailsDialogOpen(true);
  }, []);

  const handleSaveImageDetails = useCallback(async (title: string, credit: string, description: string) => {
    if (editingImageIndex === null) return;

    const image = localImages[editingImageIndex];
    if (!image) return;

    // Update local state
    setLocalImages((current) => {
      const updated = [...current];
      updated[editingImageIndex] = {
        ...updated[editingImageIndex],
        title: title || undefined,
        credit: credit || undefined,
        description: description || undefined,
      };
      return updated;
    });

    // If image has an _id (already uploaded), update it in the backend
      if (image._id) {
        try {
          await updateMediaFile({
            _id: image._id,
            title: title || null,
            credit: credit || null,
            description: description || null,
          });
        } catch (error) {
          showError("Failed to update image details. Please try again.");
      }
    }
    // If image doesn't have _id yet (not uploaded), the title/description
    // will be saved when the project is saved (we'll update after upload)
  }, [editingImageIndex, localImages, updateMediaFile, showError]);

  const { state, handleChange, errors, handleSubmit } = useFormState({
    initialState: {
      ...initialState,
      ...projectFile,
    },
    onSubmit: async (state) => {
      const newImages = localImages.filter((img) => !!!img?._id);

      // Create a FileList from the new images
      // Ensure all files are proper File instances (not Blobs)
      const filesToUpload: File[] = [];
      for (const image of newImages) {
        if (image.file) {
          const fileObj = image.file as any; // Use 'any' to check instanceof
          // If it's already a File, use it directly
          if (fileObj instanceof File) {
            filesToUpload.push(fileObj);
          } 
          // If it's a Blob, convert it to a File
          else if (fileObj instanceof Blob) {
            // Create a new File from the Blob
            // Blobs don't have a name property, so we'll generate one
            const fileName = `image-${Date.now()}.${fileObj.type.includes('png') ? 'png' : 'jpg'}`;
            const fileType = fileObj.type || 'image/jpeg';
            const newFile = new File([fileObj], fileName, { type: fileType });
            filesToUpload.push(newFile);
          }
        }
      }

      // Convert File[] to FileList using DataTransfer
      const dataTransfer = new DataTransfer();
      filesToUpload.forEach((file) => {
        try {
          dataTransfer.items.add(file);
        } catch (error) {
          console.error('Failed to add file to DataTransfer:', error, file);
        }
      });

      const fileList = dataTransfer.files;

      const uploadedImages =
        fileList.length > 0 ? await onUpload(fileList) : [];

      if (fileList.length > 0 && uploadedImages.length !== fileList.length) {
        showError("Some images failed to upload. Please try again.");
        return;
      }

      const existingIds = new Set(
        localImages
          .filter((image) => image._id)
          .map((image) => image._id as string)
      );

      const imagesToDelete = (projectFile?.images as MediaFile[] | undefined)?.filter(
        (image) => !existingIds.has((image as MediaFile)._id as string)
      );

      const hasImagesToDelete =
        imagesToDelete?.length && imagesToDelete.length > 0;

      if (hasImagesToDelete) {
        await Promise.all(
          imagesToDelete.map((image) =>
            deleteFirebaseImage(
              (image as MediaFile).url as string,
              (image as MediaFile)._id as string
            )
          )
        );
      }

      const uploadIdMap = new Map<string, string>();
      // Update MediaFiles with title/description/credit after upload
      const updatePromises: Promise<void>[] = [];
      newImages.forEach((image, index) => {
        const uploaded = uploadedImages[index];
        if (uploaded?._id) {
          uploadIdMap.set(image.local_url, uploaded._id as string);
          
          // Update MediaFile with title/description/credit if they exist
          if (image.title || image.description || image.credit) {
            updatePromises.push(
              updateMediaFile({
                _id: uploaded._id as string,
                title: image.title || null,
                credit: image.credit || null,
                description: image.description || null,
              }).then(() => {
              }).catch((error) => {
                console.error("Error updating image details after upload:", error);
              })
            );
          }
        }
      });
      
      // Wait for all updates to complete (but don't block on errors)
      if (updatePromises.length > 0) {
        await Promise.allSettled(updatePromises);
      }


      const orderedImageIds = localImages.map((image, index) => {
        if (image._id) {
          return image._id as string;
        }
        const uploadedId = uploadIdMap.get(image.local_url);
        if (!uploadedId) {
          throw new Error(`Unable to resolve uploaded image identifier for image at index ${index}.`);
        }
        return uploadedId;
      });

      newImages.forEach((image) => {
        if (image.file) {
          URL.revokeObjectURL(image.local_url);
        }
      });

      // Transform the data to ensure valid enum values
      const nextPublished = publishOverrideRef.current ?? isPublished;
      const update = {
        name: state.name || "",
        description: state.description || "",
        style: state.style || null, // Let GraphQL handle null values
        projectCategory: state.projectCategory || null,
        specialRequirements: (state.specialRequirements || []).filter(
          (requirement) => requirement !== SpecialRequirements.NotApplicable
        ),
        budgetRange: state.budgetRange || null,
        squareFootage: state.squareFootage || null,
        projectDuration: state.projectDuration || null,
        designerId: user?.id as string,
        images: orderedImageIds,
        published: nextPublished,
      };

      // Debug: Log the data being sent

      try {
        if (!isEditMode) {
          await createProjectFile(update);
          showSuccess("Project Added Successfully");
        } else {
          await updateProjectFile({
            ...update,
            _id: projectFile?._id as string,
          });
          showSuccess("Project Updated Successfully");
        }

        if (nextPublished !== isPublished) {
          setIsPublished(nextPublished);
        }
        onPublishStateChange?.(nextPublished);

        if (user?.id) {
          await refreshDesignerProjectFiles(user.id, true);
        }
        
        // Navigate back to Designer Files page
        setTimeout(() => {
          navigate(Pages.MY_DESIGN_FILES);
        }, 1000);
      } catch (error) {
        showError("Failed to save project. Please try again.");
      } finally {
        publishOverrideRef.current = null;
      }
    },
  });

  const isLoading =
    loading ||
    projectFileSaveLoading ||
    projectFileUpdateLoading ||
    deleteImageLoading;

  // Set the handleSubmit ref directly - no need for useEffect
  useEffect(() => {
    if (handleSubmitRef) {
      handleSubmitRef.current = handleSubmit;
    }
    if (handlePublishRef) {
      handlePublishRef.current = async (nextPublished: boolean) => {
        publishOverrideRef.current = nextPublished;
        const didSubmit = await handleSubmit();
        if (!didSubmit) {
          publishOverrideRef.current = null;
        }
      };
    }
  }, [handleSubmit, handleSubmitRef, handlePublishRef]);

  useEffect(() => {
    if (projectFile) {
      const nextPublished = projectFile.published ?? true;
      setIsPublished(nextPublished);
    }
  }, [projectFile]);

  useEffect(() => {
    onPublishStateChange?.(isPublished);
  }, [isPublished, onPublishStateChange]);

  // Check if all required fields are filled
  const isFormValidValue = useCallback(() => {
    const requiredFields = [
      state.name,
      state.projectCategory,
      state.style,
      state.budgetRange,
      state.squareFootage,
      state.projectDuration,
      state.description
    ];
    
    // Check if all required fields have values (not empty strings or null)
    return requiredFields.every(field => 
      field && 
      field.toString().trim() !== '' && 
      field !== null && 
      field !== undefined
    );
  }, [state.name, state.projectCategory, state.style, state.budgetRange, state.squareFootage, state.projectDuration, state.description]);

  // Update form validity whenever it changes
  useEffect(() => {
    if (onSetFormValid) {
      onSetFormValid(isFormValidValue());
    }
  }, [isFormValidValue, onSetFormValid]);

  return (
    <>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            width: "100%",
          }}
        >
          <CircularProgress sx={{ color: '#00A06D' }} size={60} />
        </Box>
      )}

      {!isLoading && (
        <Box
          className="content-block"
          sx={{
            mb: "32px",
            p: "32px",
            borderTopLeftRadius: "0px",
            borderTopRightRadius: "0px",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
            <Chip
              label={isPublished ? "Published" : "Draft"}
              size="small"
              sx={{
                backgroundColor: isPublished ? "#2E7D32" : "#ED6C02",
                color: "#fff",
                fontWeight: 600,
              }}
            />
            <Body2Typography sx={{ color: "text.secondary" }}>
              {isPublished
                ? "This project is visible in the Designer Files gallery."
                : "Publish when you're ready for this project to appear in the gallery."}
            </Body2Typography>
          </Stack>
          <Grid container spacing={3}>
            {/* Left Column - Form */}
            <Grid item xs={12} md={6}>
              <Body2Typography mb={2} fontWeight={600} sx={{ fontSize: "1rem" }}>
                Project Details
              </Body2Typography>
              <AddProjectForm
                state={state as ProjectFile}
                handleChange={handleChange}
                errors={errors}
              />
            </Grid>

            {/* Right Column - Upload Component and Project Photos */}
            <Grid item xs={12} md={6}>
              <Body2Typography mb={2} fontWeight={600} sx={{ fontSize: "1rem" }}>
                Upload Project Images
              </Body2Typography>
              <Box
                sx={{
                  position: "sticky",
                  top: "24px", // Stick to top with some offset
                }}
              >
                {/* Upload Component - Unified Container */}
                <Box
                  sx={{
                    p: 2,
                    background: "#FBFBFB", // Standardized light grey background
                    border: "1px dashed #D9D9D9", // Dashed border
                    borderRadius: "12px",
                    width: "100%", // Ensure full width
                  }}
                >
                  {/* Upload Area */}
                  <UploadProjectImage
                    multiple
                    currentCount={localImages.length}
                    maxCount={MAX_PROJECT_IMAGES}
                    onComplete={handleAddImages}
                    label="Upload Project Images"
                    sx={{ width: "100%" }} // Pass width as prop
                  />

                  {/* Project Photos Section - Only show when images exist */}
                  {localImages.length > 0 && (
                    <>
                      <Box sx={{ mt: 3, mb: 2 }}>
                        <Body2Typography mb="8px" fontWeight={600}>
                          Project Photos
                        </Body2Typography>
                        <Body2Typography sx={{ color: "text.secondary" }}>
                          Upload up to {MAX_PROJECT_IMAGES} images. Drag to reorder on desktop
                          or assign numbers on mobile. The first image will be used as the cover photo.
                        </Body2Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <ProjectImageList
                          images={localImages}
                          onReorder={setLocalImages}
                          onDelete={handleDeleteImage}
                          onSetCover={handleSetCoverImage}
                          onEditDetails={handleEditImageDetails}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Image Details Dialog */}
      {editingImageIndex !== null && localImages[editingImageIndex] && (
        <ImageDetailsDialog
          open={detailsDialogOpen}
          onClose={() => {
            setDetailsDialogOpen(false);
            setEditingImageIndex(null);
          }}
          onSave={handleSaveImageDetails}
          imageUrl={localImages[editingImageIndex].local_url}
          currentTitle={localImages[editingImageIndex].title || ""}
          currentCredit={localImages[editingImageIndex].credit || ""}
          currentDescription={localImages[editingImageIndex].description || ""}
        />
      )}
    </>
  );
};
